import Highcharts from 'highcharts';
import * as Sentry from '@sentry/browser';
import { message } from 'antd';
import logger from '@/utils/logger';
import i18n from '@/i18n/i18n';

let labelErrorAlreadyShown = false;

/**
 * Checks if the chart and axis are fully initialized and ready for label creation.
 * Prevents errors when metrics are synced from URL before chart data is loaded.
 */
const isChartReadyForLabels = (axis: Highcharts.Axis): boolean => {
  if (!axis || !axis.chart || !axis.chart.renderer) {
    return false;
  }

  const chart = axis.chart;
  const axisAny = axis as any;

  // Chart must be rendered with valid dimensions
  if (!chart.plotWidth || !chart.plotHeight || chart.plotLeft === undefined || chart.plotTop === undefined) {
    return false;
  }

  // Axis must be laid out with valid position and length
  if (
    axisAny.left === undefined ||
    axisAny.top === undefined ||
    axisAny.len === undefined ||
    !isFinite(axisAny.left) ||
    !isFinite(axisAny.top) ||
    !isFinite(axisAny.len)
  ) {
    return false;
  }

  // Axis must have valid extremes (min/max values)
  if (axis.min === undefined || axis.max === undefined || !isFinite(axis.min) || !isFinite(axis.max)) {
    return false;
  }

  return true;
};

export const wrapHighchartsLabel = (): void => {
  if ((Highcharts as unknown as { _labelWrapped: boolean })._labelWrapped) return;
  (Highcharts as unknown as { _labelWrapped: boolean })._labelWrapped = true;

  // Wrap createLabel to prevent errors when chart initializes before data is ready
  // This occurs when metrics are synced from URL parameters before data fetching completes
  Highcharts.wrap(Highcharts.Tick.prototype, 'createLabel', function (proceed, ...args) {
    if (!isChartReadyForLabels(this.axis)) {
      return null;
    }

    try {
      return proceed.apply(this, args);
    } catch (e) {
      // Silently return null - label creation will be retried when chart is ready
      return null;
    }
  });

  Highcharts.wrap(Highcharts.Tick.prototype, 'addLabel', function (proceed, ...args) {
    if (!this.axis || typeof this.pos !== 'number') return;
    if (!isChartReadyForLabels(this.axis)) return;

    try {
      proceed.apply(this, args);
    } catch (e) {
      if (!labelErrorAlreadyShown) {
        labelErrorAlreadyShown = true;
        message.error(i18n.t(`couldn't fetch data`));

        const errorMsg = `Highcharts addLabel failed: ${e?.message || e}`;
        logger(errorMsg);
        Sentry.captureException(new Error(errorMsg));
      }
      return;
    }

    // Validate label bbox if available
    if (this.label && typeof this.label.getBBox === 'function') {
      try {
        const bbox = this.label.getBBox();
        if (typeof bbox?.x !== 'number') {
          throw new Error('Invalid bbox.x in Highcharts label');
        }
      } catch (e) {
        const error = new Error(`BBox validation failed in Highcharts label: ${e}`);
        logger(error.message);
        Sentry.captureException(error);
      }
    }
  });
};
